/**
 * Portfolio Performance Report — Phase C.
 *
 * Consumes Phase B's StrategyDailySnapshot data via two endpoints:
 *   GET /api/trading/portfolio-equity-curve/?account_id=&start=&end=
 *   GET /api/trading/strategies/{id}/equity-curve/?start=&end=
 *
 * UI:
 *   Header bar:   [Account selector] [Period: 1M | 3M | YTD | 1Y | All] [Refresh]
 *   KPI tiles:    Total Return, CAGR, Sharpe, Max Drawdown, Realized P&L, Unrealized
 *   Equity chart: Portfolio equity (gold) + each member strategy (faint)
 *   Drawdown:     Underwater chart
 *   Attribution:  Per-strategy contribution table sorted by impact
 *
 * Metrics are computed client-side from daily_return arrays returned by
 * the API — the backend stays a pure data layer, the page formats and
 * derives. Annualization uses 252 trading days.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { tradingService } from '../../services/tradingService';
import { mergeLayout, PLOTLY_CONFIG } from '../../components/charts/chartTheme';

// ─── small utilities ───────────────────────────────────────────────────────
const fmt$  = (n, d = 2) => n == null || isNaN(n) ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: d }).format(n);
const fmtPct = (n, d = 2) => n == null || isNaN(n) ? '—' : `${(n * 100).toFixed(d)}%`;
const fmtNum = (n, d = 2) => n == null || isNaN(n) ? '—'
  : Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const pnlColor = (n) => n == null || isNaN(n) ? 'text-gray-400'
  : n > 0 ? 'text-green-400' : n < 0 ? 'text-red-400' : 'text-gray-400';

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

// Above this many strategy lines the Plotly legend overflows the plot area,
// so we hide it and rely on hover for line identification.
const LEGEND_FIT_LIMIT = 10;

// ─── period buttons ────────────────────────────────────────────────────────
const PERIODS = [
  { key: '1M',  label: '1M',  days:  21  },
  { key: '3M',  label: '3M',  days:  63  },
  { key: 'YTD', label: 'YTD', days: null },   // computed by date math
  { key: '1Y',  label: '1Y',  days: 252  },
  { key: 'ALL', label: 'All', days: null },
];

const periodToStart = (key) => {
  const today = new Date();
  if (key === 'YTD') {
    return new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10);
  }
  if (key === 'ALL') return null;
  const p = PERIODS.find(p => p.key === key);
  if (!p || !p.days) return null;
  const calDays = Math.round(p.days * 7 / 5);   // approx trading-day → calendar-day
  return new Date(today.getTime() - calDays * 86400000).toISOString().slice(0, 10);
};

// ─── derived metrics from a daily_return series ────────────────────────────
const computeMetrics = (series) => {
  if (!series || series.length < 2) {
    return { totalReturn: 0, cagr: 0, sharpe: 0, sortino: 0, maxDD: 0, volAnn: 0, winRate: 0, nDays: 0 };
  }
  const rets = series.map(p => p.daily_return).filter(r => r != null && !isNaN(r));
  if (!rets.length) {
    return { totalReturn: 0, cagr: 0, sharpe: 0, sortino: 0, maxDD: 0, volAnn: 0, winRate: 0, nDays: 0 };
  }
  // Flow-adjusted total return: compound the (epoch-aware) daily_return series so
  // a capital deploy/withdraw is NOT counted as return. Raw total_equity endpoints
  // would inflate this by the deployed size (same flow-contamination as Today's P&L).
  const totalReturn = rets.reduce((acc, r) => acc * (1 + r), 1) - 1;
  const nDays   = series.length;
  const years   = nDays / 252;
  const cagr    = years > 0 ? Math.pow(1 + totalReturn, 1 / years) - 1 : 0;

  // Daily stats (excluding the leading 0 from first row)
  const mu  = rets.reduce((s, r) => s + r, 0) / rets.length;
  const variance = rets.reduce((s, r) => s + (r - mu) ** 2, 0) / Math.max(rets.length - 1, 1);
  const std = Math.sqrt(variance);
  const volAnn = std * Math.sqrt(252);
  const sharpe = std > 0 ? (mu / std) * Math.sqrt(252) : 0;

  // Sortino: downside std only
  const downsideSquares = rets.filter(r => r < 0).map(r => r * r);
  const downsideStd = downsideSquares.length
    ? Math.sqrt(downsideSquares.reduce((s, x) => s + x, 0) / downsideSquares.length)
    : 0;
  const sortino = downsideStd > 0 ? (mu / downsideStd) * Math.sqrt(252) : 0;

  // Drawdown
  // Drawdown on the flow-adjusted wealth index (∏(1+r)), not raw equity — else a
  // deploy resets the peak and a withdrawal shows a phantom drawdown.
  let wealth = 1, peak = 1, maxDD = 0;
  for (const r of rets) {
    wealth *= (1 + r);
    if (wealth > peak) peak = wealth;
    const dd = peak > 0 ? (wealth - peak) / peak : 0;
    if (dd < maxDD) maxDD = dd;
  }

  // Win rate (% of days with positive return)
  const winRate = rets.length ? rets.filter(r => r > 0).length / rets.length : 0;

  return { totalReturn, cagr, sharpe, sortino, maxDD, volAnn, winRate, nDays };
};

// ─── KPI tile ──────────────────────────────────────────────────────────────
const Tile = ({ label, value, color = 'text-gray-100', sub }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3 sm:p-4">
    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className={`text-lg sm:text-xl font-semibold tabular-nums ${color}`}>{value}</div>
    {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

// ─── per-strategy attribution table row ────────────────────────────────────
const AttribRow = ({ row, totalReturn }) => {
  const pctOfPortfolio = (row.contribution / totalReturn) || 0;
  return (
    <tr className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#1c2330] text-xs">
      <td className="py-2 px-2 sm:px-3 truncate max-w-[140px]" title={row.name}>{row.name}</td>
      <td className={`text-right tabular-nums ${pnlColor(row.totalReturn)}`}>{fmtPct(row.totalReturn)}</td>
      <td className={`text-right tabular-nums hidden sm:table-cell ${pnlColor(row.sharpe)}`}>{fmtNum(row.sharpe)}</td>
      <td className="text-right tabular-nums hidden sm:table-cell">{fmt$(row.allocation_usd, 0)}</td>
      <td className={`text-right tabular-nums ${pnlColor(row.contribution)}`}>{fmtPct(row.contribution)}</td>
      <td className="text-right tabular-nums">
        <span className="text-[10px] text-gray-400 mr-1">{fmtPct(pctOfPortfolio, 1)}</span>
        of port.
      </td>
    </tr>
  );
};

// ─── main component ────────────────────────────────────────────────────────
const ACCOUNT_LS_KEY = 'maghaweer.performance.selected_account_id';

const PerformancePage = () => {
  // Account selection
  const [accounts, setAccounts] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(() => {
    const stored = localStorage.getItem(ACCOUNT_LS_KEY);
    return stored ? Number(stored) : null;
  });

  // Period filter
  const [period, setPeriod] = useState('ALL');
  const startDate = useMemo(() => periodToStart(period), [period]);

  // Data state
  const [portfolio,     setPortfolio]     = useState(null);  // { series: [...] }
  const [strategies,    setStrategies]    = useState([]);    // [{id, name, allocation_usd}]
  const [stratCurves,   setStratCurves]   = useState({});    // { [id]: series }
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [showStratLines, setShowStratLines] = useState(true);

  // Load accounts on mount
  useEffect(() => {
    tradingService.listIBKRAccounts()
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.results || []);
        setAccounts(list);
        if (selectedAccountId == null) {
          const def = list.find(a => a.is_default);
          if (def) setSelectedAccountId(def.id);
        }
      })
      .catch(() => setAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedAccountId != null) localStorage.setItem(ACCOUNT_LS_KEY, String(selectedAccountId));
  }, [selectedAccountId]);

  // Load portfolio rollup + per-strategy curves
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Portfolio rollup
      const params = { ...(startDate ? { start: startDate } : {}) };
      if (selectedAccountId != null) params.account_id = selectedAccountId;
      const portRes = await tradingService.getPortfolioEquityCurve(params);
      setPortfolio(portRes.data);

      // 2. Which strategies belong to this account? Filter the user's strategies
      const stratRes = await tradingService.getStrategiesForAccount(selectedAccountId);
      const stratList = Array.isArray(stratRes.data) ? stratRes.data : (stratRes.data?.results || []);
      setStrategies(stratList);

      // 3. Per-strategy equity curves, concurrency-bounded. A deployed
      //    portfolio can hold 478+ single-symbol strategies; firing one
      //    GET per strategy in a single Promise.all would launch hundreds
      //    of simultaneous requests at the app-box. Process in chunks so
      //    at most CONCURRENCY are in flight at once.
      const curves = {};
      const CONCURRENCY = 8;
      for (let i = 0; i < stratList.length; i += CONCURRENCY) {
        const chunk = stratList.slice(i, i + CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(chunk.map(async s => {
          try {
            const r = await tradingService.getStrategyEquityCurve(s.id, params);
            curves[s.id] = r.data.series;
          } catch (e) { /* skip */ }
        }));
      }
      setStratCurves(curves);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, startDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── derived ──────────────────────────────────────────────────────────
  const portfolioMetrics = useMemo(() => computeMetrics(portfolio?.series), [portfolio]);

  // Per-strategy metrics + contribution
  const attribution = useMemo(() => {
    const rows = strategies.map(s => {
      const series = stratCurves[s.id] || [];
      const m = computeMetrics(series);
      return {
        id: s.id,
        name: s.name,
        allocation_usd: s.allocation_usd,
        totalReturn:    m.totalReturn,
        sharpe:         m.sharpe,
        // Dollar contribution to portfolio = (final equity - initial) for this strategy
        contribution:   m.totalReturn,
      };
    });
    // Contribution = strategy's $ gain / portfolio's $ gain (weighted by allocation).
    // Simpler formulation: each strategy's return × (its_allocation / total_allocation)
    const totalAlloc = rows.reduce((s, r) => s + (r.allocation_usd || 0), 0);
    for (const r of rows) {
      r.contribution = totalAlloc > 0
        ? r.totalReturn * (r.allocation_usd / totalAlloc)
        : 0;
    }
    return rows.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
  }, [strategies, stratCurves]);

  // ── chart data ───────────────────────────────────────────────────────
  // Strategy-level palette — distinct colors so individual strategies are
  // distinguishable even when the user has 5+ of them. Gold reserved for
  // the portfolio line.
  const STRAT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

  const equityChartData = useMemo(() => {
    if (!portfolio?.series?.length) return [];
    // Y axis = cumulative return %, NOT raw $ equity. Why: portfolio total
    // ≈ N × allocation_usd while each strategy is ≈ allocation_usd, so
    // plotting $ makes the strategy lines vanish into the bottom of the
    // chart when portfolio dominates the y range. Plotting cumulative
    // return puts every line on the same 0%-anchored axis — directly
    // comparable. Switched 2026-05-21.
    const portTrace = {
      x: portfolio.series.map(p => p.date),
      y: portfolio.series.map(p => p.cumulative_return),
      type: 'scatter', mode: 'lines',
      name: 'Portfolio',
      line: { color: '#c9a96e', width: 3 },     // gold accent
      hovertemplate: '%{x}<br>%{y:.2%}<extra>Portfolio</extra>',
    };
    if (!showStratLines) return [portTrace];
    // Cap plotted strategy lines to the top-N by allocation. With 478
    // single-symbol strategies, drawing every line yields an unreadable
    // tangle (only 8 colors) and a legend that overflows the layout. The
    // attribution table below still lists every strategy.
    const MAX_LINES = 20;
    const stratTraces = strategies
      .filter(s => stratCurves[s.id]?.length)
      .slice()
      .sort((a, b) => (b.allocation_usd || 0) - (a.allocation_usd || 0))
      .slice(0, MAX_LINES)
      .map((s, idx) => ({
        x: stratCurves[s.id].map(p => p.date),
        y: stratCurves[s.id].map(p => p.cumulative_return),
        type: 'scatter', mode: 'lines',
        name: s.name.length > 25 ? s.name.slice(0, 23) + '…' : s.name,
        line: { width: 1.5, color: STRAT_COLORS[idx % STRAT_COLORS.length] },
        opacity: 0.85,
        hovertemplate: '%{x}<br>%{y:.2%}<extra>%{fullData.name}</extra>',
      }));
    return [...stratTraces, portTrace];  // portfolio drawn on top
  }, [portfolio, strategies, stratCurves, showStratLines]);

  const drawdownChartData = useMemo(() => {
    if (!portfolio?.series?.length) return [];
    // Flow-adjusted: drawdown on the TWR wealth index (1 + cumulative_return), not
    // raw total_equity — a deploy/withdraw must not create a phantom peak/trough.
    let peak = 1 + (portfolio.series[0].cumulative_return || 0);
    const dd = portfolio.series.map(p => {
      const w = 1 + (p.cumulative_return || 0);
      if (w > peak) peak = w;
      return peak > 0 ? (w - peak) / peak : 0;
    });
    return [{
      x: portfolio.series.map(p => p.date),
      y: dd,
      type: 'scatter', mode: 'lines',
      name: 'Drawdown',
      line: { color: '#dc2626', width: 1.5 },
      fill: 'tozeroy', fillcolor: 'rgba(220,38,38,0.25)',
      hovertemplate: '%{x}<br>%{y:.2%}<extra>Drawdown</extra>',
    }];
  }, [portfolio]);

  const theme = isDark() ? 'dark' : 'light';
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const equityLayout = mergeLayout({
    height: isMobile ? 280 : 380,
    // Cumulative return as a percentage. Show the zeroline prominently so
    // it's obvious where breakeven sits when some strategies are negative.
    yaxis: {
      tickformat: '.1%',
      title: '',
      zeroline: true,
      zerolinewidth: 1,
      zerolinecolor: theme === 'dark' ? '#3b4a5e' : '#cbd5e1',
    },
    xaxis: { title: '' },
    // Show the legend only while it fits the plot area; past the threshold
    // it overflows, so we drop it and rely on hover to identify lines.
    showlegend: showStratLines && strategies.length >= 1 && strategies.length <= LEGEND_FIT_LIMIT,
  }, theme);
  const ddLayout = mergeLayout({
    height: isMobile ? 140 : 180,
    yaxis: { tickformat: '.1%', title: '', rangemode: 'tozero' },
    xaxis: { title: '' },
    showlegend: false,
  }, theme);
  const chartConfig = { ...PLOTLY_CONFIG, staticPlot: isMobile };

  // ── tiles values ─────────────────────────────────────────────────────
  const lastPoint = portfolio?.series?.length ? portfolio.series[portfolio.series.length - 1] : null;

  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-lg sm:text-xl text-gray-900 dark:text-gray-100 tracking-tight">
            Portfolio Performance
          </h1>
          <p className="text-[11px] text-gray-500 font-mono uppercase tracking-wider mt-0.5">
            {portfolio?.account_name || 'Loading…'}
            {lastPoint && <> · {portfolioMetrics.nDays} days · last close ${fmtNum(lastPoint.total_equity, 2)}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {accounts && accounts.length >= 2 && (
            <select
              value={selectedAccountId ?? ''}
              onChange={e => setSelectedAccountId(e.target.value ? Number(e.target.value) : null)}
              className="px-2.5 py-1.5 text-xs rounded-lg bg-white dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            >
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name} {a.is_default ? '(default)' : ''}</option>
              ))}
            </select>
          )}
          <div className="flex rounded-lg bg-gray-100 dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 overflow-hidden">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                  period === p.key
                    ? 'bg-brand-gold/80 text-black'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
          >
            {loading ? '…' : '↻'}
          </button>
        </div>
      </div>

      {/* ── Error / empty state ───────────────────────────────────────── */}
      {error && (
        <div className="p-3 text-xs text-red-300 bg-red-900/10 border border-red-700/40 rounded-lg">
          {error}
        </div>
      )}
      {!error && !loading && portfolio?.n_points === 0 && (
        <div className="text-center py-10 text-sm text-gray-500">
          No daily snapshots yet for this account / period.
          <br/>
          <span className="text-[11px] text-gray-600">
            Snapshots accumulate from <code className="font-mono">snapshot_strategies_daily</code> running at 5:55 PM ET.
            Run the backfill command on EC2 to populate historical days.
          </span>
        </div>
      )}

      {/* ── KPI tiles ─────────────────────────────────────────────────── */}
      {portfolio?.n_points > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            <Tile label="Total Return" value={fmtPct(portfolioMetrics.totalReturn)} color={pnlColor(portfolioMetrics.totalReturn)}/>
            <Tile label="CAGR"         value={fmtPct(portfolioMetrics.cagr)}        color={pnlColor(portfolioMetrics.cagr)}/>
            <Tile label="Sharpe"       value={fmtNum(portfolioMetrics.sharpe, 2)}   color={pnlColor(portfolioMetrics.sharpe)} sub={`Sortino ${fmtNum(portfolioMetrics.sortino, 2)}`}/>
            <Tile label="Max Drawdown" value={fmtPct(portfolioMetrics.maxDD)}        color={pnlColor(portfolioMetrics.maxDD)}/>
            <Tile label="Volatility"   value={fmtPct(portfolioMetrics.volAnn)}      color="text-gray-100" sub="annualized"/>
            <Tile label="Win Rate"     value={fmtPct(portfolioMetrics.winRate, 1)}  color="text-gray-100" sub={`${portfolioMetrics.nDays} days`}/>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <Tile label="Equity"          value={fmt$(lastPoint?.total_equity)}     color="text-gray-100"/>
            <Tile label="Realized P&L"    value={fmt$(lastPoint?.realized_pnl)}    color={pnlColor(lastPoint?.realized_pnl)}/>
            <Tile label="Unrealized P&L"  value={fmt$(lastPoint?.unrealized_pnl)}  color={pnlColor(lastPoint?.unrealized_pnl)}/>
            <Tile label="Active Strats"   value={lastPoint?.n_strategies || 0}     color="text-gray-100" sub={`${lastPoint?.n_positions_total || 0} positions`}/>
          </div>

          {/* ── Equity curve ──────────────────────────────────────────── */}
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-wider text-gray-400">Cumulative return</h3>
              {strategies.length >= 2 && (
                <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={showStratLines} onChange={e => setShowStratLines(e.target.checked)}/>
                  Show strategies
                </label>
              )}
            </div>
            <Plot data={equityChartData} layout={equityLayout} config={chartConfig} useResizeHandler style={{ width: '100%' }}/>
          </div>

          {/* ── Drawdown ──────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3">
            <h3 className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Drawdown</h3>
            <Plot data={drawdownChartData} layout={ddLayout} config={chartConfig} useResizeHandler style={{ width: '100%' }}/>
          </div>

          {/* ── Attribution table ─────────────────────────────────────── */}
          {attribution.length > 0 && (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-[11px] uppercase tracking-wider text-gray-400">Per-strategy attribution</h3>
              </div>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200 dark:border-gray-800">
                      <th className="text-left  py-2 px-2 sm:px-3">Strategy</th>
                      <th className="text-right">Return</th>
                      <th className="text-right hidden sm:table-cell">Sharpe</th>
                      <th className="text-right hidden sm:table-cell">Alloc</th>
                      <th className="text-right">Contribution</th>
                      <th className="text-right">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attribution.map(row => (
                      <AttribRow key={row.id} row={row} totalReturn={portfolioMetrics.totalReturn} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PerformancePage;
